import { useState, useEffect, useCallback } from 'react';
import { Loader2, CheckCircle2, AlertCircle, RefreshCw, Save, Sparkles, MailCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  fetchKlaviyoStatus,
  saveKlaviyoConfig,
  syncKlaviyoUsers,
  seedKlaviyoSchema,
  backfillKlaviyoConsent,
  type KlaviyoStatus,
} from '@/services/klaviyoService';

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('de-DE') : '–';

export const AdminIntegrationsView: React.FC = () => {
  const [status, setStatus] = useState<KlaviyoStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [listId, setListId] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await fetchKlaviyoStatus();
      setStatus(s);
      setListId(s.listId ?? '');
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Status konnte nicht geladen werden.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = async () => {
    if (!listId.trim()) {
      setMessage({ type: 'error', text: 'Bitte eine Klaviyo-List-ID angeben.' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      // apiKey leer lassen → gespeicherten Key behalten
      const s = await saveKlaviyoConfig(listId.trim(), apiKey.trim() || undefined);
      setStatus(s);
      setApiKey('');
      setMessage({ type: 'ok', text: 'Konfiguration gespeichert.' });
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Speichern fehlgeschlagen.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const res = await syncKlaviyoUsers();
      setMessage({ type: 'ok', text: `${res.syncedCount} Nutzer an Klaviyo übermittelt.` });
      await load();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Sync fehlgeschlagen.' });
    } finally {
      setSyncing(false);
    }
  };

  const handleBackfillConsent = async () => {
    if (!window.confirm(
      'Marketing-Consent für ALLE bestehenden Nutzer auf „erteilt" setzen und sie in Klaviyo als abonniert melden?\n\n' +
      'Nur ausführen, wenn der Consent extern eingeholt wurde.'
    )) return;
    setBackfilling(true);
    setMessage(null);
    try {
      const res = await backfillKlaviyoConsent();
      setMessage({
        type: 'ok',
        text: `Consent-Backfill abgeschlossen — ${res.dbUpdatedCount} Profile aktualisiert, ${res.subscribedCount} an Klaviyo als abonniert gemeldet.`,
      });
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Consent-Backfill fehlgeschlagen.' });
    } finally {
      setBackfilling(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    setMessage(null);
    try {
      const res = await seedKlaviyoSchema();
      setMessage({ type: 'ok', text: `Schema geseedet — ${res.seededMetrics} Metriken registriert.` });
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Schema-Seed fehlgeschlagen.' });
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const configured = status?.configured ?? false;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-lg font-bold text-foreground">Klaviyo</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Alle Nutzer als Profile in eine Klaviyo-Liste synchronisieren. Der API-Key wird server-seitig gespeichert und
          nie im Browser angezeigt.
        </p>
      </div>

      {message && (
        <div
          className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${
            message.type === 'ok'
              ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
              : 'border-destructive/30 bg-destructive/10 text-destructive'
          }`}
        >
          {message.type === 'ok' ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-foreground">Status:</span>
            {configured ? (
              <span className="inline-flex items-center gap-1 text-emerald-600">
                <CheckCircle2 className="h-4 w-4" /> API-Key gespeichert
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                <AlertCircle className="h-4 w-4" /> Noch kein API-Key
              </span>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Klaviyo Private API Key
            </label>
            <Input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={configured ? '•••••••• (gespeichert — leer lassen zum Behalten)' : 'pk_live_…'}
              autoComplete="off"
            />
            <p className="text-[11px] text-muted-foreground">
              Klaviyo → Settings → API Keys → Private API Key (mit List- und Profile-Rechten).
            </p>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              List ID
            </label>
            <Input
              value={listId}
              onChange={(e) => setListId(e.target.value)}
              placeholder="z. B. XyZ123"
              autoComplete="off"
            />
            <p className="text-[11px] text-muted-foreground">
              Klaviyo → Lists &amp; Segments → gewünschte Liste → „List ID" in den Einstellungen.
            </p>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Speichern
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-3">
          <div>
            <h3 className="text-sm font-bold text-foreground">Nutzer synchronisieren</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Übermittelt alle Nutzer (E-Mail, Name, Plan, Status) als Profile an die konfigurierte Liste. Bestehende
              Profile werden aktualisiert.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="rounded-lg border border-border bg-muted/30 p-2.5">
              <div className="text-muted-foreground">Letzter Sync</div>
              <div className="font-medium text-foreground">{formatDate(status?.lastSyncAt ?? null)}</div>
            </div>
            <div className="rounded-lg border border-border bg-muted/30 p-2.5">
              <div className="text-muted-foreground">Übermittelt</div>
              <div className="font-medium text-foreground">
                {status?.lastSyncCount != null ? `${status.lastSyncCount} Nutzer` : '–'}
              </div>
            </div>
          </div>

          {status?.lastSyncError && (
            <p className="text-xs text-destructive">Letzter Fehler: {status.lastSyncError}</p>
          )}

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={handleSeed}
              disabled={seeding || !configured || !status?.listId}
              className="gap-2"
            >
              {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Klaviyo-Schema seeden
            </Button>
            <Button onClick={handleSync} disabled={syncing || !configured || !status?.listId} className="gap-2">
              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Alle Nutzer jetzt synchronisieren
            </Button>
          </div>
          {(!configured || !status?.listId) && (
            <p className="text-right text-[11px] text-muted-foreground">
              API-Key und List-ID speichern, um zu synchronisieren.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5 space-y-3">
          <div>
            <h3 className="text-sm font-bold text-foreground">Consent-Backfill</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Setzt den Marketing-Consent für alle bestehenden Nutzer auf „erteilt" und meldet sie in Klaviyo als
              abonniert (SUBSCRIBED). Nur nutzen, wenn der Consent extern (ausserhalb dieser App) eingeholt wurde. Neue
              Registrierungen werden bereits automatisch als eingewilligt behandelt.
            </p>
          </div>

          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={handleBackfillConsent}
              disabled={backfilling || !configured || !status?.listId}
              className="gap-2"
            >
              {backfilling ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailCheck className="h-4 w-4" />}
              Consent für alle Nutzer setzen
            </Button>
          </div>
          {(!configured || !status?.listId) && (
            <p className="text-right text-[11px] text-muted-foreground">
              API-Key und List-ID speichern, um den Backfill auszuführen.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
